import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { UsersThree, TrashSimple, Sparkle } from "@phosphor-icons/react";

export default function DemoDataManager() {
  const { t } = useTranslation();
  const [status, setStatus] = useState({ instructors: 0, courses: 0, reviews: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/demo-coaches");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/demo-coaches/seed");
      toast.success(t("demo.seed_success", { coaches: data.coaches, reviews: data.reviews }));
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm(t("demo.clear_confirm"))) return;
    setSaving(true);
    try {
      await api.delete("/admin/demo-coaches");
      toast.success(t("demo.clear_success"));
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const hasDemo = status.instructors > 0;

  if (loading) return null;

  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-6" data-testid="demo-data-manager">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
        <div className="flex items-center gap-3">
          <UsersThree size={22} className="text-amber-500" weight="duotone" />
          <h3 className="font-display text-xl uppercase font-black">{t("demo.admin_section_title")}</h3>
          <span
            className={`inline-flex items-center px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold border ${hasDemo ? "text-amber-500 border-amber-500/40 bg-amber-500/5" : "text-zinc-400 border-white/20"}`}
            data-testid="demo-status-badge"
          >
            {hasDemo ? t("demo.admin_status_present", { n: status.instructors }) : t("demo.admin_status_none")}
          </span>
        </div>
      </div>

      <p className="text-xs text-zinc-400 max-w-3xl leading-relaxed mb-6">{t("demo.admin_section_hint")}</p>

      <div className="flex flex-wrap gap-3">
        <Button
          onClick={seed}
          disabled={saving}
          className="bg-amber-500 hover:bg-amber-400 text-black uppercase tracking-[0.2em] font-bold rounded-none h-11 px-6"
          data-testid="demo-seed-button"
        >
          <Sparkle size={14} className="mr-2" weight="fill" /> {saving ? t("demo.seed_saving") : t("demo.seed_button")}
        </Button>
        {hasDemo && (
          <Button
            onClick={clear}
            disabled={saving}
            variant="outline"
            className="border-[#E30613] text-[#E30613] hover:bg-[#E30613]/10 uppercase tracking-widest text-xs font-bold rounded-none h-11"
            data-testid="demo-clear-button"
          >
            <TrashSimple size={14} className="mr-1" weight="bold" /> {saving ? t("demo.clear_saving") : t("demo.clear_button")}
          </Button>
        )}
      </div>

      {hasDemo && (
        <div className="mt-5 pt-5 border-t border-white/5 grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="font-display text-3xl font-black text-amber-500">{status.instructors}</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mt-1">Coach</div>
          </div>
          <div>
            <div className="font-display text-3xl font-black text-white">{status.courses}</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mt-1">Corsi</div>
          </div>
          <div>
            <div className="font-display text-3xl font-black text-white">{status.reviews}</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mt-1">Reviews</div>
          </div>
        </div>
      )}
    </div>
  );
}
